import type { RawProfile } from 'lumen/utils/net-revenue/cost/types';

/**
 * MOCK — the cost-profile API does not exist yet, so the list is backed by this
 * in-memory data. Shapes are raw API payloads (snake_case, embedded costs with
 * flat cost_values) so they flow through the real serializer + transform.
 *
 * To switch to the real API: delete this file AND
 * `app/adapters/cost-setting/profile.ts`. The model then falls back to
 * `ApplicationAdapter` (real HTTP). Nothing else references the mock.
 *
 * Persists for the browser session; reset on reload. Tests call
 * `resetMockProfiles()` / `seedMockProfiles()` for determinism.
 */

function simpleCommission(
  ...costs: { id: string; value: number }[]
): RawProfile['costs'] {
  return costs.map(({ id, value }) => ({
    id,
    commission_type: 'simple',
    cost_type: 'commission',
    cost_values: [
      { leadtime_end: null, leadtime_start: null, value, weekday: null },
    ],
  }));
}

function defaultProfiles(): RawProfile[] {
  return [
    {
      id: '1',
      name: 'OTA Standard Commission',
      description: 'Default commission applied to OTA channels.',
      created_at: '2026-01-15T10:00:00Z',
      updated_at: '2026-02-02T09:30:00Z',
      costs: simpleCommission({ id: 'c1', value: 15 }),
    },
    {
      id: '2',
      name: 'Direct Booking',
      description: 'No commission on direct reservations.',
      created_at: '2026-01-20T14:00:00Z',
      updated_at: '2026-01-20T14:00:00Z',
      costs: simpleCommission({ id: 'c2', value: 0 }),
    },
    {
      id: '3',
      name: 'Wholesale',
      description: 'Higher commission for wholesale partners.',
      created_at: '2026-02-10T08:15:00Z',
      updated_at: '2026-03-01T11:45:00Z',
      costs: simpleCommission({ id: 'c3', value: 22 }, { id: 'c3b', value: 5 }),
    },
    {
      id: '4',
      name: 'GDS Commission',
      description: 'Commission for GDS-sourced bookings.',
      created_at: '2026-03-05T16:20:00Z',
      updated_at: '2026-03-05T16:20:00Z',
      costs: simpleCommission({ id: 'c4', value: 18 }),
    },
    {
      id: '5',
      name: 'Unconfigured Profile',
      description: null,
      created_at: '2026-03-12T12:00:00Z',
      updated_at: '2026-03-12T12:00:00Z',
      costs: [],
    },
  ];
}

function clone(profiles: RawProfile[]): RawProfile[] {
  return JSON.parse(JSON.stringify(profiles)) as RawProfile[];
}

let profiles: RawProfile[] = defaultProfiles();

export function resetMockProfiles(): void {
  profiles = defaultProfiles();
}

export function seedMockProfiles(seed: RawProfile[]): void {
  profiles = clone(seed);
}

export function mockCostProfiles(): { profiles: RawProfile[] } {
  return { profiles: clone(profiles) };
}
